/**
 * Rejoin Game Dialog
 *
 * Non-closable dialog that appears when user returns to landing page
 * while they have an active game in progress.
 * Blocks all other actions until user rejoins the game.
 */

import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { PillButton } from '@/shared/components/PillButton';

interface RejoinGameDialogProps {
  isOpen: boolean;
  gameType: 'free' | 'paid';
  isLoading: boolean;
  onRejoin: () => void;
  error: string | null;
}

export function RejoinGameDialog({
  isOpen,
  gameType,
  isLoading,
  onRejoin,
  error
}: RejoinGameDialogProps) {
  const { t } = useTranslation();

  // Use green variant for free games, yellow for paid games
  const dialogVariant = gameType === 'paid' ? 'yellow' : 'green';

  return (
    <Dialog
      isOpen={isOpen}
      variant={dialogVariant}
      title={t('rejoin.title')}
      showCloseButton={false}
      allowClickOutsideToClose={false}
    >
      <div className="flex flex-col gap-6">
        <p className="text-center text-gray-600 text-base">{t('rejoin.message')}</p>
        {error && <p className="text-center text-red-500 text-sm">{error}</p>}
        <PillButton
          variant="green"
          onClick={onRejoin}
          disabled={isLoading}
          className="w-full h-12"
          icon={
            <span
              className="font-mind-boggle text-[#F2F2F2] leading-none px-2"
              style={{
                fontSize: '1.1rem',
                fontWeight: 1000,
                textShadow:
                  '2px 2px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000'
              }}
            >
              {isLoading ? t('rejoin.loading') : t('rejoin.button')}
            </span>
          }
        />
      </div>
    </Dialog>
  );
}
